import React, { useContext, useEffect, useState } from 'react'

import {
  COLUMN_NAMES as INQUIRY_COLUMN_NAMES,
  Inquiry,
  InquiryFetchArgs,
  addOrUpdateInquiry,
  getInquiryViews,
} from '../../assets/ts/data/inquiry/inquiry'
import {
  STATUS_CODE,
  STATUS_CODE__COMPLETED,
  STATUS_CODE__IN_PROGRESS,
} from '../../assets/ts/data/inquiry/inquiry-status-metadata'
import { twoDigits } from '../../assets/ts/formatting/formatting'
import Alert from '../../components/alert/alert'
import PageOverlayBusy from '../../components/page-overlay/page-overlay-busy'
import CustomerDashboardInquiresTable from '../../components/table/customer-dashboard-inqiries-table'
import Tabs, { TabItem } from '../../components/tabs/tabs'
import useInquiries from '../../hooks/inquiry/use-inquiries'
import useInquiryCount from '../../hooks/inquiry/use-inquiry-count'
import useInquiryDistinctValues from '../../hooks/inquiry/use-inquiry-distinct-values'
import useInquiryStatusCounts from '../../hooks/inquiry/use-inquiry-status-counts'
import usePageAccumulator from '../../hooks/usePageAccumulator'
import { SelectionContext } from '../../store/selection-context-provider'
import CustomerDashboardInquiryFilters from './customer-dashboard-inquiries-filters'

type Props = {
  filter: InquiryFetchArgs
  isSavedInquiryDeleted: boolean
  onClearAll: () => void
  onUpdateFilter: (args: InquiryFetchArgs , order: number) => void
  tabSelected : number
}

const CustomerDashboardInquiries = ({
  filter,
  isSavedInquiryDeleted,
  onClearAll,
  onUpdateFilter,
  tabSelected,
}: Props) => {
  const selection = useContext(SelectionContext)
  const [KPIFilter, setKPIFilter] = useState<InquiryFetchArgs>({})
  const [KPITabSelected, setKPITabSelected] = useState(0)
  const [pageNum, setPageNum] = useState(0)
  const [exportFetchIsBusy, setExportFetchIsBusy] = useState(false)

  const updateKpiFilter = (updatedArgs: InquiryFetchArgs) => {
    setPageNum(0)
    setKPIFilter((priorArgs) => {
      return {
        ...priorArgs,
        ...updatedArgs,
      }
    })
  }

  const inquiryPaging: InquiryFetchArgs = {
    pageNumber: pageNum,
  }

  const { inquiryStatusCounts, inquiryStatusCountsIsLoading } = useInquiryStatusCounts(filter)

  const { inquiryCount: customerActionInquiryCount } = useInquiryCount({
    ...filter,
    customerActionRequiredFilter: true,
  })
  const { inquiries, inquiriesAreLoading } = useInquiries({
    ...filter,
    statusCodesOmittedFilter: [STATUS_CODE.SAVED],
    ...KPIFilter,
    ...inquiryPaging,
  })
  const {
    inquiryCountIsLoading: kpiFilteredInquiriesCountIsLoading,
    inquiryCountValue: kpiFilteredInquiriesCount,
  } = useInquiryCount({
    ...filter,
    statusCodesOmittedFilter: [STATUS_CODE.SAVED],
    ...KPIFilter,
  })

  const kpiFilteredInquiriesCountString = kpiFilteredInquiriesCount.toLocaleString() ?? '0'

  const countByStatus = (statusCodes?: string[], invert = false) => {
    const filterFunc = invert
      ? (item) => !statusCodes || !statusCodes.includes(item.StatusCode)
      : (item) => !statusCodes || statusCodes.includes(item.StatusCode)

    const count = inquiryStatusCounts
      .filter(filterFunc)
      .reduce((acc, item) => item.InquiryGUID_count + acc, 0)
    const countString = count.toLocaleString()

    return countString
  }

  const actionRequiredCountString =
    customerActionInquiryCount?.[0]?.InquiryGUID_count.toLocaleString() ?? '0'

  const handleKpiTabSelection = (tabNumber) => {
    selection.reset()
    setKPITabSelected(tabNumber)
    switch (tabNumber) {
      case 0: // Total Inquiries
        updateKpiFilter({
          customerActionRequiredFilter: false,
          statusCodesFilter: [],
          statusCodesOmittedFilter: [STATUS_CODE.SAVED],
        })
        break
      case 1: // In Progress
        updateKpiFilter({
          customerActionRequiredFilter: false,
          statusCodesFilter: STATUS_CODE__IN_PROGRESS,
          statusCodesOmittedFilter: [],
        })
        break
      case 2: // Completed
        updateKpiFilter({
          customerActionRequiredFilter: false,
          statusCodesFilter: STATUS_CODE__COMPLETED,
          statusCodesOmittedFilter: [],
        })
        break
      case 3: // Action Required
        updateKpiFilter({
          customerActionRequiredFilter: true,
          statusCodesFilter: [],
          statusCodesOmittedFilter: [STATUS_CODE.SAVED],
        })
        break
      case 4: // Saved
        updateKpiFilter({
          customerActionRequiredFilter: false,
          statusCodesFilter: [STATUS_CODE.SAVED],
          statusCodesOmittedFilter: [],
        })
        break
    }
  }

  const accumulatedInquiries = usePageAccumulator(pageNum, inquiries)

  useEffect(() => {
    selection.mergeWithAllLoadedRecordKeys(inquiries.map((i) => i.InquiryGUID ?? ''))
  }, [inquiries])

  const handleBottomScrollReached = () => {
    if (kpiFilteredInquiriesCount > accumulatedInquiries.length) {
      setPageNum((num) => num + 1)
    }
  }

  const { inquiryDistinctValues } = useInquiryDistinctValues({
    assignedComponentCodeFilter: filter.assignedComponentCodeFilter,
    currentlyServicingByFilter: filter.currentlyServicingByFilter,
    distinctFiltersToQuery: [
      INQUIRY_COLUMN_NAMES.FY,
      INQUIRY_COLUMN_NAMES.AssignedComponentCode,
      INQUIRY_COLUMN_NAMES.InquiryType,
      INQUIRY_COLUMN_NAMES.ServicingMAJCOMCode,
      INQUIRY_COLUMN_NAMES.CurrentlyServicingBy,
      INQUIRY_COLUMN_NAMES.ServicingOrganization,
      INQUIRY_COLUMN_NAMES.SectionCode,
    ],
    inquiryTypeFilter : filter.inquiryTypeFilter,
    isArchivedFilter: filter.isArchivedFilter,
    relatedUserFilter: filter.relatedUserFilter,
    sectionCodeFilter: filter.sectionCodeFilter,
    servicingMAJCOMCodeFilter: filter.servicingMAJCOMCodeFilter,
    servicingOrganizationFilter: filter.servicingOrganizationFilter,
    yearFilter: filter.yearFilter,
  })
  const [showConfirmationAlert, setShowConfirmationAlert] = useState(false)
  const [selectedItem, setselectedItem] = useState<string>()
  const deleteItems = async (selectedItem: string) => {
    setselectedItem(selectedItem)
    setShowConfirmationAlert(true)
  }
  const deleteConfirmationAlert = (button: string): void => {
    if (button === 'OK') {
      deleteConformedInquiries()
    }
    setShowConfirmationAlert(false)
  }
  const deleteConformedInquiries = async () => {
    const deletedinquiry = accumulatedInquiries.find(
      (item) => (item.InquiryGUID as string) === selectedItem
    )
    if (deletedinquiry) {
      await addOrUpdateInquiry({
        IsDeleted: true,
        eKey: deletedinquiry.eKey,
        eTag: deletedinquiry.eTag,
      })
      onUpdateFilter({ isSavedInquiryDeleted: !isSavedInquiryDeleted } , 100)
    }
  }
  const CustomerInqiryitems: TabItem[] = [
    {
      content: (
        <CustomerDashboardInquiresTable
          count={twoDigits(kpiFilteredInquiriesCountString)}
          countNumber={kpiFilteredInquiriesCount}
          getAllSelectedRecords={getAllSelectedRecords}
          inquiries={accumulatedInquiries}
          onBottomScrollReached={handleBottomScrollReached}
          title='Total Inquiries'
        />
      ),
      count: twoDigits(countByStatus([STATUS_CODE.SAVED], /* invert */ true)),
      isActive: KPITabSelected === 0,
      tabTitle: 'Total Inquiries',
    },
    {
      content: (
        <CustomerDashboardInquiresTable
          count={twoDigits(kpiFilteredInquiriesCountString)}
          countNumber={kpiFilteredInquiriesCount}
          getAllSelectedRecords={getAllSelectedRecords}
          inquiries={accumulatedInquiries}
          onBottomScrollReached={handleBottomScrollReached}
          title='In Progress'
        />
      ),
      count: twoDigits(countByStatus(STATUS_CODE__IN_PROGRESS)),
      isActive: KPITabSelected === 1,
      tabTitle: 'In Progress',
    },
    {
      content: (
        <CustomerDashboardInquiresTable
          count={twoDigits(kpiFilteredInquiriesCountString)}
          countNumber={kpiFilteredInquiriesCount}
          getAllSelectedRecords={getAllSelectedRecords}
          inquiries={accumulatedInquiries}
          onBottomScrollReached={handleBottomScrollReached}
          title='Completed'
        />
      ),
      count: twoDigits(countByStatus(STATUS_CODE__COMPLETED)),
      isActive: KPITabSelected === 2,
      tabTitle: 'Completed',
    },
    {
      content: (
        <CustomerDashboardInquiresTable
          count={twoDigits(kpiFilteredInquiriesCountString)}
          countNumber={kpiFilteredInquiriesCount}
          getAllSelectedRecords={getAllSelectedRecords}
          inquiries={accumulatedInquiries}
          onBottomScrollReached={handleBottomScrollReached}
          title='Action Required'
        />
      ),
      count: twoDigits(actionRequiredCountString),
      isActive: KPITabSelected === 3,
      tabTitle: 'Action Required',
    },
    {
      content: (
        <CustomerDashboardInquiresTable
          count={twoDigits(kpiFilteredInquiriesCountString)}
          countNumber={kpiFilteredInquiriesCount}
          deleteItems={deleteItems}
          getAllSelectedRecords={getAllSelectedRecords}
          inquiries={accumulatedInquiries}
          onBottomScrollReached={handleBottomScrollReached}
          title='Saved'
        />
      ),
      count: twoDigits(countByStatus([STATUS_CODE.SAVED])),
      isActive: KPITabSelected == 4,
      tabTitle: 'Saved',
    },
  ]

  const handleDashboardFilterChange = (e , order) => {
    selection.reset()
    if (onUpdateFilter) {
      setPageNum(0)
      onUpdateFilter(e , order)
    }
  }

  const busy =
    inquiriesAreLoading ||
    kpiFilteredInquiriesCountIsLoading ||
    inquiryStatusCountsIsLoading ||
    exportFetchIsBusy

  async function getAllSelectedRecords(): Promise<Inquiry[]> {
    const visibleSelectedRecords = accumulatedInquiries.filter((d) =>
      selection.keyIsSelected(d.InquiryGUID ?? '')
    )

    const unloadedSelectedRecords = await getAllUnloadedSelectedRecords()

    const allSelectedRecords = [...visibleSelectedRecords, ...unloadedSelectedRecords]

    return allSelectedRecords
  }

  async function getAllUnloadedSelectedRecords(): Promise<Inquiry[]> {
    if (selection.unloadedRecordsAreSelected) {
      setExportFetchIsBusy(true)
      // Becuase the user clicked the Select All in the past, all records
      // matching the current dashboard and KPI filters that are in pages
      // past the currently loaded/visible ones need to be fetched
      const items = await getInquiryViews({
        ...filter,
        statusCodesOmittedFilter: [STATUS_CODE.SAVED],
        ...KPIFilter,
        onePageOnly: false,
        pageNumber: (inquiryPaging.pageNumber ?? 0) + 1,
      })
      setExportFetchIsBusy(false)
      return items
    } else {
      return []
    }
  }
  useEffect(()=>{
    setKPIFilter({})
    setKPITabSelected(0)
    setPageNum(0)
    selection.reset()
    onClearAll()
  },[tabSelected])

  return (
    <>
      {busy && <PageOverlayBusy />}
      {showConfirmationAlert && (
        <Alert
          alerttextclass='poppins-semibold font-13 text-center'
          btn1className='btn btn btn-sm font-12 poppins-semibold btn-primary whitetext border-radius text-uppercase'
          btn1iconclassNmae='icon-checked font-12 pe-1'
          btn2classNmae='btn btn btn-border1 darktext btn-sm poppins-semibold font-12 border-radius text-uppercase'
          btn2iconclassNmae='icon-close color-primary font-12 pe-1'
          cancel='Cancel'
          className='alert-info'
          message={'Are you sure you want to delete the selected item?'}
          onClick={deleteConfirmationAlert}
          yes='OK'
        />
      )}
      <div className='d-flex flex-column card-body h-100'>
        <div className='d-flex justify-content-between align-items-start align-items-sm-center flex-wrap'>
          <CustomerDashboardInquiryFilters
            availableFilterValues={inquiryDistinctValues[0]}
            filter={filter}
            onClearAll={() => {
              setKPIFilter({})
              setKPITabSelected(0)
              setPageNum(0)
              selection.reset()
              onClearAll()
            }}
            onUpdateFilter={handleDashboardFilterChange}
            tabSelected={tabSelected}
          />
        </div>
        <Tabs
          anchorclass='card d-flex kpiborder justify-content-between card-body h-100 dashboard-kpi-title px-2 d-flex align-items-center poppins-semibold font-12 title-color13 border1'
          badge='border-left1 p-2 color-primary d-flex align-items-center poppins-bold font-22 mx-2'
          className='tabKpis'
          items={CustomerInqiryitems}
          onTabSelected={handleKpiTabSelection}
          tablistclassName='font-13 col'
        />
      </div>
    </>
  )
}
export default CustomerDashboardInquiries
